import { type ReactElement } from 'react'
import { proxy, useSnapshot } from 'valtio'

import { logMessage } from '../../Logging/log'

import './Notices.scss'

type Notice = {
  id: string
  message: string
  content?: ReactElement
  cta?: {
    label: string
    link?: string
    onClick?: (n: Notice) => void
    dismissOnClick?: boolean
  }
  close: () => void
  expirationMillis?: number
  isClosable?: boolean
}

const noticesProxy = proxy<{
  queued: Notice[]
  showing: Notice[]
  isShown: boolean
}>({
  queued: [],
  showing: [],
  isShown: false,
})

const timeOuts: Record<Notice['id'], number> = {}

const MAX_SHOWN_NOTICES = 3

const updateShownNotices = () => {
  const noticesToAdd = Math.max(
    0,
    MAX_SHOWN_NOTICES - noticesProxy.showing.length
  )

  for (let i = 0; i < noticesToAdd; i += 1) {
    const notice = noticesProxy.queued.shift()

    if (!notice) break

    noticesProxy.showing.push(notice)

    if (notice.expirationMillis) {
      timeOuts[notice.id] = window.setTimeout(
        notice.close,
        notice.expirationMillis
      )
    }
  }
}

const showNotice = (notice: Omit<Notice, 'id' | 'close'>) => {
  const noticeId = crypto.randomUUID()

  logMessage(`User shown notice — ${notice.message}`, {
    level: 'info',
    extra: {
      ...notice,
    },
  })

  const closeNotice = () => {
    const timeOut = timeOuts[noticeId]
    if (timeOut) {
      window.clearTimeout(timeOut)
      delete timeOuts[noticeId]
    }

    const noticeShownIdx = noticesProxy.showing.findIndex(
      (n) => n.id === noticeId
    )
    if (noticeShownIdx !== -1) noticesProxy.showing.splice(noticeShownIdx, 1)

    const noticeQueuedIdx = noticesProxy.queued.findIndex(
      (n) => n.id === noticeId
    )
    if (noticeQueuedIdx !== -1) noticesProxy.queued.splice(noticeQueuedIdx, 1)

    updateShownNotices()
  }

  noticesProxy.queued.push({
    ...notice,
    id: noticeId,
    close: closeNotice,
  })
  updateShownNotices()

  return closeNotice
}

const useNotices = () => {
  return useSnapshot(noticesProxy)
}

const setNoticesShown = () => {
  noticesProxy.isShown = true
}

const isNoticesShown = () => {
  return noticesProxy.isShown
}

export { useNotices, setNoticesShown, showNotice, isNoticesShown, type Notice }
